import moment from 'moment';

const ISO_DAY = /^\d{4}-\d{2}-\d{2}$/;
const ISO_DAY_PREFIX = /^\d{4}-\d{2}-\d{2}/;

/** TN ingest bug: years written 50 years ahead (2076 vs 2026). Interim read-time fallback only —
 *  does NOT fix rescheduled events (wrong month/day). Run
 *  scripts/repair-tn-listing-event-dates-from-catalog.js to write correct TN Catalog dates. */
const TN_YEAR_OFFSET_BUG = 50;
const TN_YEAR_OFFSET_BUG_MIN = 2070;
const TN_YEAR_OFFSET_BUG_MAX = 2099;

const normalizeListingEventYear = year => {
  if (year >= TN_YEAR_OFFSET_BUG_MIN && year <= TN_YEAR_OFFSET_BUG_MAX) {
    return year - TN_YEAR_OFFSET_BUG;
  }
  return year;
};

/**
 * Correct known TN year-offset bugs in ISO date strings and Flex date objects.
 */
export const normalizeListingEventDateString = raw => {
  if (raw == null) {
    return raw;
  }
  if (typeof raw === 'object') {
    const year = Number(raw.year);
    if (Number.isFinite(year)) {
      const corrected = normalizeListingEventYear(year);
      if (corrected !== year) {
        return { ...raw, year: corrected };
      }
    }
    return raw;
  }
  const s = String(raw).trim();
  if (!s || s === '[object Object]') {
    return s;
  }
  const isoMatch = s.match(/^(\d{4})(-\d{2}-\d{2}(?:T[\d:.+-Z]+)?.*)?$/);
  if (isoMatch) {
    const year = parseInt(isoMatch[1], 10);
    const corrected = normalizeListingEventYear(year);
    if (corrected !== year) {
      return `${corrected}${isoMatch[2] || ''}`;
    }
  }
  return s;
};

const OTHER_PUBLIC_DATE_KEYS = [
  'event_date',
  'startDate',
  'start_date',
  'date',
  'eventStart',
  'event_start',
];

export const isTicketNetworkEventListing = publicData => {
  const pd = publicData;
  if (!pd || typeof pd !== 'object') {
    return false;
  }
  return (
    pd.ticketNetworkEventListing === true ||
    pd.ticketNetworkListingMode === 'event' ||
    !!pd.ticketNetworkEventId
  );
};

export const extractListingEventCalendarDay = raw => {
  if (raw == null || String(raw).trim() === '') {
    return null;
  }
  const s = String(normalizeListingEventDateString(raw)).trim();
  if (ISO_DAY.test(s)) {
    return s;
  }
  const m = s.match(ISO_DAY_PREFIX);
  return m ? m[0] : null;
};

const unixFromEventDateUnixField = unix => {
  if (unix == null || String(unix).trim() === '') {
    return null;
  }
  const n = Number(unix);
  if (!Number.isFinite(n)) {
    return null;
  }
  return n < 1e12 ? Math.floor(n) : Math.floor(n / 1000);
};

export const utcStartOfDayUnixFromCalendarDay = yyyyMmDd => {
  if (!yyyyMmDd || !ISO_DAY.test(yyyyMmDd)) {
    return null;
  }
  const m = moment.utc(yyyyMmDd, 'YYYY-MM-DD', true);
  if (!m.isValid()) {
    return null;
  }
  return m.startOf('day').unix();
};

const calendarDayFromPublicDataKeys = (pd, keys) => {
  if (!pd || typeof pd !== 'object') {
    return null;
  }
  for (const key of keys) {
    const day = extractListingEventCalendarDay(pd[key]);
    if (day) {
      return day;
    }
  }
  return null;
};

const calendarDayFromEventDateUnix = unix => {
  const fromUnix = unixFromEventDateUnixField(unix);
  if (fromUnix == null) {
    return null;
  }
  return extractListingEventCalendarDay(moment.unix(fromUnix).utc().format('YYYY-MM-DD'));
};

/**
 * When TN `eventStartDate` disagrees with `eventDate` / `eventDateUnix`, trust the two
 * fields that agree. Stale start dates (e.g. 2023) otherwise sort to the top of
 * "soonest first" search and the listing page says the event has passed.
 */
const consensusTicketNetworkCalendarDay = pd => {
  const startDay = extractListingEventCalendarDay(pd.eventStartDate);
  const eventDay = extractListingEventCalendarDay(pd.eventDate);
  const unixDay = calendarDayFromEventDateUnix(pd.eventDateUnix);
  if (eventDay && unixDay && eventDay === unixDay && startDay !== eventDay) {
    return eventDay;
  }
  if (startDay && eventDay && startDay === eventDay) {
    return startDay;
  }
  if (startDay && unixDay && startDay === unixDay) {
    return startDay;
  }
  return startDay || eventDay || unixDay || null;
};

/**
 * YYYY-MM-DD calendar day for a listing's event (TN: eventStartDate before eventDate,
 * unless startDate is the outlier against eventDate + eventDateUnix).
 */
export const listingEventCalendarDayFromPublicData = publicData => {
  const pd = publicData;
  if (!pd || typeof pd !== 'object') {
    return null;
  }

  if (isTicketNetworkEventListing(pd)) {
    const consensus = consensusTicketNetworkCalendarDay(pd);
    if (consensus) {
      return consensus;
    }
    return calendarDayFromPublicDataKeys(pd, OTHER_PUBLIC_DATE_KEYS);
  }

  const fromPrimary = calendarDayFromPublicDataKeys(pd, ['eventDate', 'eventStartDate']);
  if (fromPrimary) {
    return fromPrimary;
  }

  return calendarDayFromPublicDataKeys(pd, OTHER_PUBLIC_DATE_KEYS);
};

/**
 * Unix seconds (UTC start-of-day) for search sort / pub_eventDateUnix index.
 */
export const listingEventUnixFromPublicData = (publicData, { preferTicketNetworkDates } = {}) => {
  const pd = publicData;
  if (!pd || typeof pd !== 'object') {
    return null;
  }

  const isTn =
    preferTicketNetworkDates === true ||
    (preferTicketNetworkDates !== false && isTicketNetworkEventListing(pd));

  if (isTn) {
    const day = listingEventCalendarDayFromPublicData(pd);
    if (day) {
      const unix = utcStartOfDayUnixFromCalendarDay(day);
      if (unix != null) {
        return unix;
      }
    }
  }

  const fromUnix = unixFromEventDateUnixField(pd.eventDateUnix);
  if (fromUnix != null) {
    const dayFromUnix = extractListingEventCalendarDay(
      moment.unix(fromUnix).utc().format('YYYY-MM-DD')
    );
    if (dayFromUnix) {
      const unix = utcStartOfDayUnixFromCalendarDay(dayFromUnix);
      if (unix != null) {
        return unix;
      }
    }
    return fromUnix;
  }

  const eventDate = extractListingEventCalendarDay(pd.eventDate);
  if (eventDate) {
    const unix = utcStartOfDayUnixFromCalendarDay(eventDate);
    if (unix != null) {
      return unix;
    }
  }

  const keys = ['eventStartDate', ...OTHER_PUBLIC_DATE_KEYS];
  for (const key of keys) {
    const day = extractListingEventCalendarDay(pd[key]);
    if (day) {
      const unix = utcStartOfDayUnixFromCalendarDay(day);
      if (unix != null) {
        return unix;
      }
    }
    const raw = normalizeListingEventDateString(pd[key]);
    if (raw != null && String(raw).trim() !== '') {
      const t = Date.parse(String(raw).trim());
      if (Number.isFinite(t)) {
        return Math.floor(t / 1000);
      }
    }
  }

  return null;
};

/**
 * Values to persist so Marketplace API `pub_eventDateUnix` sort matches the event.
 */
export const deriveListingEventDateForIndex = publicData => {
  const day = listingEventCalendarDayFromPublicData(publicData);
  if (!day) {
    return null;
  }
  const eventDateUnix = utcStartOfDayUnixFromCalendarDay(day);
  if (eventDateUnix == null) {
    return null;
  }
  return { eventDate: day, eventDateUnix };
};

const eventDateRawFromPublicData = publicData => {
  const pd = publicData;
  if (!pd || typeof pd !== 'object') {
    return null;
  }
  const keys = isTicketNetworkEventListing(pd)
    ? ['eventStartDate', 'eventDate', 'calendarDay', ...OTHER_PUBLIC_DATE_KEYS]
    : ['eventDate', 'eventStartDate', 'calendarDay', ...OTHER_PUBLIC_DATE_KEYS];
  for (const key of keys) {
    const raw = normalizeListingEventDateString(pd[key]);
    if (raw != null && String(raw).trim() !== '') {
      return String(raw).trim();
    }
  }
  return null;
};

export const eventDateHasExplicitTime = value =>
  /\d{1,2}:\d{2}/.test(String(value || '')) || /T\d{2}/.test(String(value || ''));

/** Same "Event date:" line parsing as venue/city SEO event lists. */
export const eventDateFromListingDescription = description => {
  const match = String(description || '').match(/^\s*Event date:\s*([^\r\n]+)/im);
  return match ? normalizeListingEventDateString(match[1].trim()) : null;
};

/** Fix TN description lines that echo the +50-year ingest bug. */
export const sanitizeListingDescriptionEventDates = (description, publicData) => {
  if (!description || !isTicketNetworkEventListing(publicData || {})) {
    return description;
  }
  return String(description)
    .replace(/^(\s*Event date:\s*)([^\r\n]+)/gim, (_, prefix, value) => {
      const normalized = normalizeListingEventDateString(String(value).trim());
      return `${prefix}${normalized}`;
    })
    .replace(/^(\s*Event end:\s*)\[object Object\]\s*$/gim, '');
};

/**
 * Resolve event moment + whether a showtime is present (mirrors venueInventory / cityService).
 */
export const listingEventDateInfo = ({ publicData, description } = {}) => {
  const pd = publicData;
  const day =
    eventDateFromListingDescription(description) ||
    (pd &&
      normalizeListingEventDateString(
        pd.eventStartDate || pd.eventDate || pd.calendarDay
      ));
  if (day) {
    const m = moment(day);
    if (m.isValid()) {
      return {
        date: m,
        hasTime: eventDateHasExplicitTime(day),
      };
    }
  }

  const raw = eventDateRawFromPublicData(pd);
  if (raw) {
    const m = moment(raw);
    if (m.isValid()) {
      return {
        date: m,
        hasTime: eventDateHasExplicitTime(raw),
      };
    }
  }

  const unix = pd?.eventDateUnix;
  if (unix != null && Number.isFinite(Number(unix))) {
    const date = moment.unix(Number(unix)).utc();
    if (date.isValid()) {
      return { date, hasTime: false };
    }
  }

  return null;
};

/** Venue upcoming-events label format: "Jan 1, 2026 6:00 PM" or "Jan 1, 2026". */
export const formatListingEventDateDisplay = dateInfo => {
  if (!dateInfo?.date?.isValid?.()) {
    return null;
  }
  return dateInfo.date.format(dateInfo.hasTime ? 'MMM D, YYYY h:mm A' : 'MMM D, YYYY');
};

const normalizeListingEventDateInput = input => {
  if (!input || typeof input !== 'object') {
    return { publicData: input };
  }
  if (input.publicData != null || input.description != null) {
    return input;
  }
  return { publicData: input };
};

/**
 * Human-readable event date/time for checkout and listing UI.
 * Accepts listing.attributes, { publicData, description }, or publicData alone.
 * Matches venue page event list labels (includes showtime when available).
 */
export const formatListingEventDateTime = input => {
  const info = listingEventDateInfo(normalizeListingEventDateInput(input));
  return formatListingEventDateDisplay(info);
};
